import { useMemo } from 'react'
import { PatientStatus } from '@valerahealth/rtk-query'
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  DrChronoIcon,
  NextGenIcon,
  MmrIcon,
  LumaIcon
} from '@valerahealth/ui-components'
import { format, formatIsoDate } from '@valerahealth/ui-components/utils/date'
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@valerahealth/ui-components/icons'
import { useParams } from 'react-router-dom'
import { useTranslation } from '@valerahealth/ui-translation'
import { useTreatmentActionsMenu } from 'components/common/CMTreatmentActionsMenu'
import {
  TreatmentRoomRouteContext,
  getProfileName,
} from '../utilities/treatments'
import { dateFormatEnum, genderEnum2 } from '../utilities/enums'

const ProfileStatus = ({
  status,
  reason,
}: {
  status: PatientStatus
  reason?: string | null
}) => {
  const { t } = useTranslation()

  const { reasonLabel, color } = useMemo(() => {
    let reasonLabel
    let color: 'error' | 'warning' | 'info' = 'error'

    if (status === PatientStatus.Waitlist) {
      color = 'info'
    } else if (
      status === PatientStatus.Pending ||
      status === PatientStatus.AwaitingInitial ||
      status === PatientStatus.Lead
    ) {
      color = 'warning'
    } else if (status === PatientStatus.ClosedDischarged) {
      if (reason === `Don't Readmit`)
        reasonLabel = t(`PatientStatus.dischargedDoNotReadmit`)

      if (reason === `Never in Service`) {
        reasonLabel = t(`PatientStatus.dischargedNeverInService`)
        color = 'warning'
      }
    }

    return {
      reasonLabel,
      color,
    }
  }, [reason, status, t])

  return (
    <Chip
      size="small"
      color={color}
      label={`${t(status)}${reasonLabel ? `: ${reasonLabel}` : ''}`}
    />
  )
}

const TreatmentHeader = ({
  patient,
  nextAppt,
  lastAppt,
}: Partial<TreatmentRoomRouteContext>) => {
  const treatmentId = useParams().treatmentId!
  const { t } = useTranslation()
  const { TreatmentActionsMenuPopups, TreatmentActionsMenu } =
    useTreatmentActionsMenu()
  const { integration: treatmentEmrStatus } = patient || {}
  const { nextgenId, lumaId, mmrId } = treatmentEmrStatus || {}

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          zIndex: 1,
          width: '100%',
          borderRadius: 0,
          p: 2,
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Avatar src={patient?.demographics?.picture || undefined} />
          <div>
            <Typography variant="subtitle2">
              {getProfileName(patient?.demographics)}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="flex"
              alignItems="center"
              gap={1}
            >
              {t('MRN')} {patient?.demographics?.mrn}{' '}
              {mmrId && (
                <MmrIcon
                  mmrId={mmrId}
                  title={`Patient mMR ID: ${mmrId} (open in mMR)`}
                  href={`${process.env.MMR_BASE_URL}/patients/${mmrId}/overview`}
                  iconStyle={{ height: 17 }}
                />
              )}
              {nextgenId && (
                <NextGenIcon
                  iconStyle={{ height: 12 }}
                  alt="NextGen"
                  title={`Patient Nextgen ID: ${nextgenId}`}
                />
              )}
              {lumaId && (
                <LumaIcon
                  lumaId={lumaId}
                  iconStyle={{ height: 12 }}
                  title={`Luma ID: ${lumaId}`}
                />
              )}
              <DrChronoIcon {...treatmentEmrStatus} />
            </Typography>
          </div>
        </Stack>
        <Divider flexItem orientation="vertical" />
        <Stack gap={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Typography
              variant="caption"
              component="p"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <PhoneIcon fontSize="small" />
              {patient?.demographics?.primaryPhone}
            </Typography>
            <Typography
              variant="caption"
              component="p"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <EmailIcon fontSize="small" />
              {patient?.demographics?.primaryEmail || '-'}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Typography component="p" variant="caption">
              <Typography color="text.secondary" variant="caption">
                {t('DOB')}:&nbsp;{' '}
              </Typography>
              {patient?.demographics?.dateOfBirth
                ? formatIsoDate(
                    patient?.demographics?.dateOfBirth,
                    dateFormatEnum.SMALL_DATE_BASIC,
                  )
                : '-'}
            </Typography>
            <Typography component="p" variant="caption">
              <Typography color="text.secondary" variant="caption">
                {t('sex')}:&nbsp;
              </Typography>

              {(patient?.demographics?.gender &&
                genderEnum2[patient?.demographics?.gender]) ||
                '-'}
            </Typography>
            <Typography component="p" variant="caption">
              <Typography color="text.secondary" variant="caption">
                {t('pronouns')}:&nbsp;
              </Typography>
              {patient?.demographics?.pronouns || '-'}
            </Typography>
          </Stack>
        </Stack>
        <Divider flexItem orientation="vertical" />
        <Box mr="auto">
          <Typography component="p" variant="caption">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                marginRight: '5px',
              }}
            >
              {t('next')}:&nbsp;
            </Typography>
            {nextAppt
              ? `${format(nextAppt.start, dateFormatEnum.SMALL_DATE_BASIC)}`
              : '-'}
          </Typography>
          <Typography component="p" variant="caption">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                marginRight: '5px',
              }}
            >
              {t('last')}:&nbsp;
            </Typography>
            {lastAppt
              ? `${format(lastAppt.start, dateFormatEnum.SMALL_DATE_BASIC)}`
              : '-'}
          </Typography>
        </Box>

        {patient?.demographics?.status &&
          patient?.demographics?.status !== PatientStatus.Active && (
            <ProfileStatus
              status={patient?.demographics?.status}
              // reason={treatment?.dischargeInfo?.reason?.trim()}
            />
          )}

        {patient && patient.demographics && (
            <TreatmentActionsMenu
              {...{
                treatmentId,
                patientId: patient?.demographics?.patientId ?? '',
                firstName: patient?.demographics?.firstName ?? '',
                lastName: patient?.demographics?.lastName ?? '',
                mrn: patient?.demographics?.mrn,
                emrId: treatmentEmrStatus?.emrId,
                lumaId: treatmentEmrStatus?.lumaId,
                nextgenId: treatmentEmrStatus?.nextgenId,
                isIntegrationActive: treatmentEmrStatus?.isActive,
                videoLink: patient?.demographics?.videoLink,
                phone: patient?.demographics?.primaryPhone ?? '',
                email: patient?.demographics?.primaryEmail ?? '',
                status: patient?.demographics?.status,
              }}
            />
        )}
      </Paper>
      <TreatmentActionsMenuPopups />
    </>
  )
}

export default TreatmentHeader
